import type { ComponentType } from 'react';

// Generic resource item interface - must have id
export interface ResourceItem {
  id: string;
}

// Panel component props
export interface PanelComponentProps<T extends ResourceItem = ResourceItem> {
  item: T;
  onClose?: () => void;
}

// Panel component type
export type PanelComponent<T extends ResourceItem = ResourceItem> = ComponentType<
  PanelComponentProps<T>
>;

// Tab key format: "resourceType:id"
export type TabKey = `${string}:${string}`;

// Tab item for detail panel
export interface TabItem<T extends ResourceItem = ResourceItem> {
  component: PanelComponent<T>;
  item: T;
  key: TabKey;
  resourceId: string;
  resourceType: string;
  label?: string;
}

// List Panel Store Types
export interface ListPanelCoreState {
  focusedId: string | null;
  selectedIds: Set<string>;
}

export interface ListPanelDerivedState {
  hasSelection: boolean;
  isMultiSelection: boolean;
  isSingleSelection: boolean;
  selectionCount: number;
}

export interface ListPanelPreferenceState {
  enableMultiSelect: boolean;
  persistSelection: boolean;
}

export interface ListPanelLayoutGuardState {
  defaultPanelWidth: number;
  maxPanelWidth: number;
  minPanelWidth: number;
}

export interface ListPanelActions {
  clearSelection: () => void;
  pushSelection: (ids: string[], replace?: boolean) => void;
  reset: () => void;
  setFocusedId: (id: string | null) => void;
  setLayoutGuard: (layoutGuard: Partial<ListPanelLayoutGuardState>) => void;
  setPreference: (preference: Partial<ListPanelPreferenceState>) => void;
}

export type ListPanelStore = ListPanelCoreState &
  ListPanelDerivedState &
  ListPanelPreferenceState &
  ListPanelLayoutGuardState &
  ListPanelActions;

// Split Detail Panel Store Types
export interface SplitDetailPanelState<T extends ResourceItem = ResourceItem> {
  activeTabKey: TabKey | null;
  tabs: TabItem<T>[];
  visible: boolean;
}

export interface SplitDetailPanelActions<T extends ResourceItem = ResourceItem> {
  closeAllTabs: (resourceType?: string) => void;
  closeTab: (key: TabKey) => void;
  openTab: (
    resourceType: string,
    item: T,
    component: PanelComponent<T>,
    activate?: boolean,
    label?: string,
  ) => void;
  reconcileTabs: (availableIdsByType: Record<string, string[]>) => void;
  reset: () => void;
  setVisible: (visible: boolean) => void;
  switchTab: (key: TabKey) => void;
}

export type SplitDetailPanelStore<T extends ResourceItem = ResourceItem> =
  SplitDetailPanelState<T> & SplitDetailPanelActions<T>;

// Constants
export const MAX_TABS = 1;

export const DEFAULT_LIST_PANEL_PREFERENCE: ListPanelPreferenceState = {
  enableMultiSelect: true,
  persistSelection: false,
};

export const DEFAULT_LIST_PANEL_LAYOUT_GUARD: ListPanelLayoutGuardState = {
  minPanelWidth: 400,
  maxPanelWidth: 800,
  defaultPanelWidth: 600,
};
